import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LineFolder } from '../../shared/models/folder/lineFolder.model';
import { ArticleInterface, ArticleType } from '../../shared/models/article/article.interface';
import { DossierPrestationsService } from '../../shared/services/dossierPrestations.service';
import { LoadingService } from '../../shared/services/loading.service';
import { ArticlesService } from '../../shared/services/articles.service';
import { BeneficiairesService } from '../../shared/services/beneficiaires.service';
import { ResponseMessage } from '../../shared/models/responseMessage/responseMessage.model';
import { MessageService } from '../../shared/services/message.service';
import { NavController } from 'ionic-angular';
import { ToastDirective } from '../../shared/directives/toast/toast';
import { Subscription } from 'rxjs/Subscription';
import { PieceJointeService } from '../../shared/services/pieceJointe.service';
import { Observable } from 'rxjs/Observable';
import { BeneficiaireContextService, PlatformContextService } from '../../shared/services/context';
import { Beneficiaire } from '../../shared/models/beneficiaire/beneficiaire.model';
import { AttachmentInterface } from '../../shared/models/base64File/attachmentInterface';
import { Folders } from '../../shared/models/folder/folder.interface';
import { LineFolders } from '../../shared/models/folder/lineFolder.interface';
import LineFolderInterface = LineFolders.LineFolderInterface;
import FolderInterface = Folders.FolderInterface;
import { Base64FileInterface } from '../../shared/models/base64File/base64File.interface';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { FormUtilsService } from '../../shared/services/form-utils.service';

@Component({
  selector: 'core-form-folder-line',
  templateUrl: './form-folder-line.component.html'
})
export class FormFolderLineComponent implements OnInit {
  @Input() private article: ArticleInterface;
  @Input() private folder: FolderInterface = null;
  @Input() public line: LineFolderInterface = null;

  public lineForm: FormGroup;
  public formErrors: any = {};
  public ArticleType = ArticleType;
  public amounts = {
    rsfMontantUniteFacture: null,
    rsfMontantUnitePrisEnCompte: null,
    totalUnitesPresente: null,
    totalUnitesPrisEnCompte: null,
    subventionTheorique: null,
    subventionReel: null
  };
  public ayantDroits: Array<any>;
  public files: AttachmentInterface[] = [];
  public filesToDelete: any = [];
  private msgValid: string = 'Dossier de prestation ajouté';
  private maxDate: string;
  private currentRequestAmount: Subscription = null;
  public beneficiaire?: Beneficiaire;
  public isEditable: boolean = false;
  public showAdditionalInfo: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private dossierPrestationsService: DossierPrestationsService,
    private beneficiaireContext: BeneficiaireContextService,
    private loadingService: LoadingService,
    private articleService: ArticlesService,
    private beneficiairesService: BeneficiairesService,
    private pieceJointeService: PieceJointeService,
    public toast: ToastDirective,
    public navCtrl: NavController,
    private messageService: MessageService,
    public platformContextService: PlatformContextService,
    private lsService: LocalStorageService,
  ) {
  }

  ngOnInit(): void {
    if (null === this.line) {
      this.line = new LineFolder({} as LineFolderInterface);
    }

    if (this.platformContextService.isElu()) {
      this.isEditable = LineFolders.isEditable(this.line);
    } else {
      this.isEditable =
        null === this.folder ||
        null !== this.folder &&
        this.folder.statutAdministratif === Folders.StatusAdministratifEnum.EnCours;
    }

    // Auto-fill calculated values on line edit
    this.amounts.rsfMontantUniteFacture = this.line.rsfMontantUniteFacture;
    this.amounts.rsfMontantUnitePrisEnCompte = this.line.rsfMontantUnitePrisEnCompte;
    this.amounts.totalUnitesPresente = this.line.rsfTotalUnitesPresente;
    this.amounts.totalUnitesPrisEnCompte = this.line.rsfTotalUnitesPrisEnCompte;
    this.amounts.subventionTheorique = this.line.subventionTheorique;
    this.amounts.subventionReel = this.line.montant;

    this.buildForm();

    if (this.line.id) {
      this.loadingService.start();
      this.dossierPrestationsService.getLineAttachments(this.line.id, this.folder.id).subscribe(
        attachments => {
          this.files = attachments.data;
        },
        error => console.log('attachments.get.error', error),
        () => this.loadingService.end()
      );
    }

    this.beneficiaire = this.beneficiaireContext.get();
  }

  private buildForm(): void {
    this.lineForm = this.formBuilder.group({
      beneficiaire: [this.line.beneficiaire ? this.line.beneficiaire.id : null],
      libelle: [this.line.libelle, [Validators.required,  Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]],
      rsfDateFacture: [this.line.rsfDateFacture, Validators.required],
      reference: [this.line.reference],
      rsfNomFournisseur: [this.line.rsfNomFournisseur],

      // Champs pour article type UNITE
      rsfNombreUniteFacture: [this.line.rsfNombreUniteFacture],
      rsfNombreUnitePrisEnCompte: [this.line.rsfNombreUnitePrisEnCompte],

      // Champs pour article type FACT / UNITE
      rsfMontantFacture: [this.line.rsfMontantFacture],
      rsfMontantFacturePrisEnCompte: [this.line.rsfMontantFacturePrisEnCompte],

      // rsfPlafondRemboursementUnite: [this.line.rsfMontantUnitePrisEnCompte, Validators.required],
      subventionUnitaireTheorique: [null, Validators.required],
      subventionUnitaire: [null, Validators.required],
      remarquePublic: [this.line.remarquePublic]
    }, {
      validator: (formGroup) => {
        if (this.ArticleType.TypeEnum.Facture === this.article.rsfType) {
          return Validators.required(formGroup.controls.rsfNomFournisseur)
            && Validators.required(formGroup.controls.rsfMontantFacture)
            && Validators.required(formGroup.controls.rsfMontantFacturePrisEnCompte);
        } else if (this.ArticleType.TypeEnum.Unite === this.article.rsfType) {
          return Validators.required(formGroup.controls.rsfNombreUniteFacture)
            && Validators.required(formGroup.controls.rsfNombreUnitePrisEnCompte);
        }
        return null;
      }
    });

    if (this.platformContextService.isElu()) {
      this.lineForm.addControl('remarquePrive', new FormControl(this.line.remarquePrive));
    }

    this.lineForm.controls.subventionUnitaireTheorique.setValue(this.line.subventionUnitaireTheorique);
    this.lineForm.controls.subventionUnitaire.setValue(this.line.subvention);

    if (this.article.nominatif) {
      this.beneficiairesService.getAyantDroits(this.article.id)
        .subscribe(
          data => {
            this.ayantDroits = data;
            this.loadingService.end();
          },
        );
    }

    const d = new Date();
    this.maxDate = new Date(d.setFullYear(d.getFullYear() + 1, d.getMonth(), d.getDay())).toISOString();

    // Disable field if the line is refused or validated
    if (this.isLineDisabled()) {
      this.lineForm.disable();

      this.lineForm.get('remarquePublic').enable();
      if (this.platformContextService.isElu()) {
        this.lineForm.get('remarquePrive').enable();
      }
    }
  }

  private canCalculateRefund(): boolean {
    const hasDate = !!this.lineForm.value.rsfDateFacture;

    // Check the beneficiary is provided (if neccessary)
    const hasBeneficiaryIfRequired =
      (this.article.nominatif && this.lineForm.value.beneficiaire) ||
      (!this.article.nominatif && !this.lineForm.value.beneficiaire);

    // Check the neccessary values are provided for ArticleType.TypeEnum.Facture
    const hasRequiredFactureFields =
      this.ArticleType.TypeEnum.Facture === this.article.rsfType
      && !!this.lineForm.value.rsfMontantFacturePrisEnCompte;

    // Check the neccessary values are provided for ArticleType.TypeEnum.Unite
    const hasRequiredUniteFields =
      this.ArticleType.TypeEnum.Unite === this.article.rsfType &&
      !!this.lineForm.value.rsfNombreUnitePrisEnCompte &&
      !!this.lineForm.value.rsfMontantFacturePrisEnCompte
      ;

    return !this.isLineDisabled() && hasDate && hasBeneficiaryIfRequired && (hasRequiredFactureFields || hasRequiredUniteFields);
  }

  public formatForm(form) {
    form.quantite = this.ArticleType.TypeEnum.Facture === this.article.rsfType ? 1 : this.lineForm.value.rsfNombreUnitePrisEnCompte;
    form.article = this.article.id;

    // Remove these fields from posted values (only used for form validation)
    delete form.subventionUnitaireTheorique;
    delete form.subventionUnitaire;

    return form;
  }

  public calculateRefund($event): void {
    // On fait l'arrondi à deux décimales ($event est défini que pour montant)
    if ($event) {
      const val = $event.value.toString();
      if (val &&
        val.lastIndexOf('.') !== val.length - 1 &&
        val.lastIndexOf('.0') !== val.length - 2 &&
        val.lastIndexOf('.00') !== val.length - 3) {
        const nbDecimales = val.indexOf('.') !== -1 ? val.length - val.lastIndexOf('.') - 1 : 0;
        // Les parseFloat et toPrecision sont pour le pb de floating point number en js
        let number = Math.floor(parseFloat((parseFloat(($event.value * 10).toPrecision(12)) * 10).toPrecision(12)));
        number = number / 100.0;
        let strVal = number.toString();
        // Si on a enlevé des décimales, on les remets pour faire moins bizarre
        const nbNewDecimales = strVal.indexOf('.') !== -1 ? strVal.length - strVal.lastIndexOf('.') - 1 : 0;
        for (let d = nbNewDecimales; d < nbDecimales && d < 2; d++) {
          if (strVal.indexOf('.') === -1) {
            strVal += '.';
          }
          strVal += '0';
        }
        $event.value = strVal;
      }
    }
    if (true === this.canCalculateRefund()) {
      const formatFormValue: any = this.formatForm(JSON.parse(JSON.stringify(this.lineForm.value)));
      // TODO : Erreur avec ces 3 champs
      // Remove these fields from subsidy call, only used when saving the line
      delete formatFormValue.libelle;
      delete formatFormValue.rsfNomFournisseur;
      delete formatFormValue.reference;
      delete formatFormValue.rsfNombreUniteFacture;
      delete formatFormValue.rsfMontantFacture;
      // Add current folder to request
      if (this.folder) {
        formatFormValue.dossierPrestation = this.folder.id;
      }

      if (this.currentRequestAmount !== null) {
        this.currentRequestAmount.unsubscribe();
      }

      this.currentRequestAmount = this.dossierPrestationsService.getApiAmount(formatFormValue)
        .subscribe(
          data => {
            this.amounts.rsfMontantUniteFacture = data.rsfMontantUniteFacture;
            this.amounts.rsfMontantUnitePrisEnCompte = data.rsfMontantUnitePrisEnCompte;
            this.amounts.totalUnitesPresente = data.totalUnitesPresente;
            this.amounts.totalUnitesPrisEnCompte = data.totalUnitesPrisEnCompte;
            this.amounts.subventionTheorique = data.subventionTheorique;
            this.amounts.subventionReel = data.subventionReel;

            this.lineForm.controls.subventionUnitaireTheorique.setValue(data.subventionTheorique);
            this.lineForm.controls.subventionUnitaire.setValue(data.subventionReel);

            this.loadingService.end();
          },
          error => this.handleError(error)
        );
    } else {
      // Reset fields if the subsidy can't be calculated to prevent wrong validations
      this.amounts.totalUnitesPresente = null;
      this.amounts.totalUnitesPrisEnCompte = null;
      this.amounts.subventionTheorique = null;
      this.amounts.subventionReel = null;

      this.lineForm.controls.subventionUnitaireTheorique.setErrors({ 'incorrect': true });
      this.lineForm.controls.subventionUnitaire.setErrors({ 'incorrect': true });
    }
  }

  getButtons(): Array<any> {
    return [
      {
        context: 'salarie',
        conditionDisplay: (LineFolders.isEditable(this.line) &&
          (null === this.folder || Folders.StatusAdministratifEnum.EnCours === this.folder.statutAdministratif)) ||
          this.isLineDisabled(),
        conditionLabel: true,
        labelIf: 'Continuer',
        action: 'addLine',
        disabled: this.lineForm.invalid,
        class: ''
      },
      {
        context: 'salarie',
        conditionDisplay: true === this.isLineDisabled(),
        conditionLabel: true,
        labelIf: 'Fermer',
        action: 'returnToFolder',
        disabled: false,
        class: ''
      },
      {
        context: 'elu',
        conditionDisplay: true,
        conditionLabel: true,
        labelIf: 'Continuer',
        action: 'addAndWaiting',
        disabled: this.lineForm.invalid,
        class: ''
      },
      {
        context: 'elu',
        conditionDisplay: true === this.isLineDisabled(),
        conditionLabel: true,
        labelIf: 'Fermer',
        action: 'returnToFolder',
        disabled: false,
        class: ''
      }
    ];
  }

  private isLineDisabled = () => {
    if (this.platformContextService.isElu()) {
      return LineFolders.StatutAdministratifEnum.Refuse === this.line.statutAdministratif
        || LineFolders.StatutAdministratifEnum.Valide === this.line.statutAdministratif;
    } else if (this.platformContextService.isSalarie()) {
      return this.folder && this.folder.id && Folders.StatusAdministratifEnum.EnCours !== this.folder.statutAdministratif;
    }
    return false;
  }

  public onKeydown(event) {
    if (true === /^[A-Z\s\n\r\,]$/i.test(event.key)) {
      // Invalid character, prevent input
      event.preventDefault();
      return;
    }
  }

  private addLine(): void {
    this.loadingService.start();

    // If we can't save, we can update the remarks
    if (this.isLineDisabled()) {
      const dirtyValues = FormUtilsService.getDirtyValues(this.lineForm);
      if (FormUtilsService.hasOnlyKeys(dirtyValues, ['remarquePublic'])) {
        const remarksValues = {
          remarquePublic: this.lineForm.value.remarquePublic
        };
        this.handleLine(this.dossierPrestationsService.updateLineRemarques(remarksValues, this.line.id, this.folder.id));
      } else {
        this.toast.create('Vous ne pouvez modifier que les remarques');
      }
    } else {
      this.autocompletePanelFields();
      const formatFormValue: any = this.formatForm(JSON.parse(JSON.stringify(this.lineForm.value)));

      if (this.lsService.getDateDossier() !== null) {
        formatFormValue.dateDossier = this.lsService.getDateDossier();
      }

      if (this.line.id) {
        this.handleLine(this.dossierPrestationsService.updateLine(formatFormValue,
          this.line.id, null === this.folder ? null : this.folder.id));
      } else {
        this.handleLine(this.dossierPrestationsService.addLine(formatFormValue, null === this.folder ? null : this.folder.id));
      }
    }
  }

  private addAndWaiting(): void {
    this.loadingService.start();

    // If we can't save, we can update the remarks
    if (this.isLineDisabled()) {
      const dirtyValues = FormUtilsService.getDirtyValues(this.lineForm);
      if (FormUtilsService.hasOnlyKeys(dirtyValues, ['remarquePublic', 'remarquePrive'])) {
        const remarksValues = {
          remarquePublic: this.lineForm.value.remarquePublic,
          remarquePrive: this.lineForm.value.remarquePrive
        };
        this.handleLine(this.dossierPrestationsService.updateLineRemarques(remarksValues, this.line.id, this.folder.id));
      } else {
        this.toast.create('Vous ne pouvez modifier que les remarques');
      }
    } else {
      this.autocompletePanelFields();
      const formatFormValue: any = this.formatForm(JSON.parse(JSON.stringify(this.lineForm.value)));

      if (this.lsService.getDateDossier() !== null) {
        formatFormValue.dateDossier = this.lsService.getDateDossier();
      }

      if (this.line.id) {
        this.handleLine(this.dossierPrestationsService.updateLine(formatFormValue,
          this.line.id, null === this.folder ? null : this.folder.id));
      } else {
        this.handleLine(this.dossierPrestationsService.addLineAndWaiting(formatFormValue, null === this.folder ? null : this.folder.id));
      }
    }
  }

  // private addAndValidate(): void {
  //   this.loadingService.start();
  //   const formatFormValue: any = this.formatForm(JSON.parse(JSON.stringify(this.lineForm.value)));

  //   if (this.line.id) {
  //     this.handleLine(this.dossierPrestationsService.updateLine(formatFormValue,
  //       this.line.id, null === this.folder ? null : this.folder.id));
  //   } else {
  //     this.handleLine(this.dossierPrestationsService.addLineAndValidated(formatFormValue, null === this.folder ? null : this.folder.id));
  //   }
  // }

  private returnToFolder(): void {
    this.navCtrl.push('FolderSummaryFormPage', { 'folderId': this.folder.id });
  }

  private autocompletePanelFields() {
    // If the fields have no value on form submission, auto-fill with the corresponding field values
    if (!this.lineForm.controls.rsfNombreUniteFacture.value) {
      this.lineForm.controls.rsfNombreUniteFacture.setValue(this.lineForm.controls.rsfNombreUnitePrisEnCompte.value);
    }
    if (!this.lineForm.controls.rsfMontantFacture.value) {
      this.lineForm.controls.rsfMontantFacture.setValue(this.lineForm.controls.rsfMontantFacturePrisEnCompte.value);
    }
    if (!this.lineForm.controls.reference.value && this.lineForm.controls.libelle.value) {
      this.lineForm.controls.reference.setValue(this.lineForm.controls.libelle.value.substring(0, 20));
    }
  }

  /**
   * Generic function to handle add to folder action
   * @param {Observable<any>} request
   */
  private handleLine(request: Observable<any>): void {
    request.subscribe(
      response => {
        this.line = response.data;
        this.saveFiles(response.data.consommation);
        this.handleSuccess(response);
      },
      error => this.handleError(error),
      () => this.loadingService.end()
    );
  }

  private saveFiles(folder) {
    // Delete files that have been removed from the line and that where previously uploaded
    for (let i = 0; i < this.filesToDelete.length; i++) {
      this.dossierPrestationsService.deleteLineAttachment(this.line.id, folder.id, this.filesToDelete[i]).subscribe(
        result => console.log('deleteLinePJ.success', result),
        error => console.log('deleteLinePJ.error', error)
      );
    }

    // Upload files that don't yet have an id
    for (let i = 0; i < this.files.length; i++) {
      if (this.files[i] && !this.files[i].id) {
        this.dossierPrestationsService.addLineAttachment(this.line.id, folder.id, this.files[i]).subscribe(
          result => console.log('addLinePJ.success', result),
          error => console.log('addLinePJ.error', error)
        );
      }
    }
  }

  private handleSuccess(data) {
    const siteResponse: ResponseMessage = data;
    let message;

    if ('message' in siteResponse && siteResponse.message) {
      message = siteResponse.message;
    } else {
      message = this.msgValid;
    }
    this.toast.create(message, 3000, 'success');

    this.lsService.removeDateDossier();

    const idFolder = data.data.consommation ? data.data.consommation.id : data.data.id;

    if (this.navCtrl.length() > 1) {
      // On va sur le dossier, et une fois fait on enleve la page d'avant
      this.goToFolder(idFolder).then(() => {
        const length: number = this.navCtrl.length();
        this.navCtrl.remove(length - 2, 1);
      });
      // this.navCtrl.pop().then(() => this.goToFolder(idFolder));
    } else {
      this.goToFolder(idFolder);
    }
  }

  private goToFolder(idFolder: number) {
    return this.navCtrl.push('FolderSummaryFormPage', { 'folderId': idFolder });
  }

  private handleError(data) {
    const error = data.json();
    if ('errors' in error) {
      const messageError = error.errors.errors ? error.errors.errors : error.message;
      if (messageError && messageError !== 'Validation Failed') {
        this.toast.createAdv({ message: messageError, type: 'error' });
        return;
      }
    }
    const response = this.messageService.handleError(data);
    this.formErrors = response.formErrors;
    this.loadingService.end();
  }

  onAdded(event: { base64File: Base64FileInterface, fileName: string, fileType: string }, fileNumber) {
    this.files[fileNumber] = event.base64File;
  }

  onDeleted(fileIndex) {
    const idAttachment = this.files[fileIndex] ? this.files[fileIndex].id : null;
    if (idAttachment) {
      this.filesToDelete.push(idAttachment);
    }
    delete this.files[fileIndex];
  }

  onClick(fileNumber) {
    if (this.files[fileNumber] && this.files[fileNumber].id) {
      this.pieceJointeService.getDownloadTicket(this.files[fileNumber].id).subscribe(resp => {
        const ticket = resp.data;
        const attachment = this.pieceJointeService.getFolderLineAttachment(
          this.line.id,
          this.folder.id,
          this.files[fileNumber].id,
          ticket
        );
        window.open(attachment, '_system');
      });
    }
  }

  /**
   * Show information message depending on context and button pressed
   * @param fieldName
   */
  public showHelp(fieldName) {
    let message;
    if ('rsfNombreUniteFacture' === fieldName) {
      if (this.platformContextService.isElu()) {
        message = 'A titre d\'information : Nombre d\'unités du justificatif présenté dans le cas où la totalité n\'est pas' +
          ' éligible au remboursement. Dans ce cas, saisissez le nombre d\'unités éligibles (nombre sur lequel la subvention' +
          ' sera calculée) dans le champ Nbr unités. Les quotas éventuels seront calculés sur ce champ.';
      } else if (this.platformContextService.isSalarie()) {
        message = 'A titre d\'information : Nombre d\'unités du justificatif présenté dans le cas où la totalité n\'est pas' +
          ' éligible au remboursement. Ces informations seront contrôlées par votre Comité.';
      }
    } else if ('rsfMontantFacture' === fieldName) {
      if (this.platformContextService.isElu()) {
        message = 'A titre d\'information : Montant total du justificatif présenté dans le cas où la totalité n\'est pas éligible' +
          ' au remboursement. Dans ce cas, saisissez le montant éligible (montant de base sur lequel la subvention sera calculée)' +
          ' dans le champ Montant.';
      } else if (this.platformContextService.isSalarie()) {
        message = 'A titre d\'information : Montant total du justificatif présenté dans le cas où la totalité n\'est pas' +
          ' éligible au remboursement. Ces informations seront contrôlées par votre Comité.';
      }
    }

    if (message) {
      this.toast.createAdv({ message: message, type: 'success' });
    }
  }
}



// WEBPACK FOOTER //
// ./src/core/form-folder-line/form-folder-line.component.ts